import { useState, useCallback, useEffect, useRef } from "react";

const VALID_MODES = [
  "characters", "quotes", "music", "locations",
  "trivia", "crafts", "silhouette",
  "results", "profile", "factions", "collection", "archive",
];

function parseDateParam(str) {
  if (!str) return null;
  const parts = str.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (isNaN(date.getTime())) return null;
  return date;
}

function formatDateParam(date) {
  if (!date) return null;
  return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
}

function readParams() {
  const params = new URLSearchParams(window.location.search);
  const modeRaw = params.get("mode");
  const mode = VALID_MODES.includes(modeRaw) ? modeRaw : "characters";
  const date = parseDateParam(params.get("date"));
  return { mode, date };
}

function writeParams(mode, date) {
  const params = new URLSearchParams();
  if (mode && mode !== "characters") params.set("mode", mode);
  if (date) params.set("date", formatDateParam(date));
  const qs = params.toString();
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState({ mode, date: formatDateParam(date) }, "", newUrl);
}

export default function useUrlParams() {
  const initial = readParams();
  const [activeTab, setActiveTabState] = useState(initial.mode);
  const [targetDateObj, setTargetDateObjState] = useState(initial.date);
  const syncing = useRef(false);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState((prev) => {
      if (prev !== tab) syncing.current = true;
      return tab;
    });
  }, []);

  const setTargetDateObj = useCallback((date) => {
    setTargetDateObjState((prev) => {
      const prevStr = formatDateParam(prev);
      const nextStr = formatDateParam(date);
      if (prevStr !== nextStr) syncing.current = true;
      return date;
    });
  }, []);

  useEffect(() => {
    if (!syncing.current) return;
    syncing.current = false;
    writeParams(activeTab, targetDateObj);
  }, [activeTab, targetDateObj]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && e.state.mode) {
        setActiveTabState(e.state.mode);
      } else {
        const params = readParams();
        setActiveTabState(params.mode);
        setTargetDateObjState(params.date);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return { activeTab, setActiveTab, targetDateObj, setTargetDateObj };
}
